import React, { useState } from 'react';
import { Sort } from '@components/Sort';
import { Button } from '@components/main-components/button/Button';
import { UserCommentItemSkeleton } from '@components/main-components/user/UserCommentItem';
import {
  getFragment,
  MintComponentFragmentDoc,
  type SongViewFragment,
  SortOrder,
} from '@graphql/reactQuery';
import { SectionTitle } from '../shared';
import { CommentBar } from './CommentBar';
import { CommentItem } from './CommentItem';
import { useReleaseComments } from './useReleaseComments';

const SORT_TYPES = ['ASC', 'DESC'] satisfies SortOrder[];

export function CommentsPreview({
  releaseFrag,
  onSeeAll,
}: {
  releaseFrag: SongViewFragment;
  onSeeAll: () => void;
}) {
  const mintRelease = getFragment(MintComponentFragmentDoc, releaseFrag);
  const releaseId = mintRelease.id;
  const [sort, setSort] = useState<SortOrder>(SortOrder.Desc);
  const { data, loading, hasNextPage } = useReleaseComments({
    releaseId,
    pageSize: 6,
    sortDirection: sort,
  });

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SectionTitle title="Comments" />
        </div>
        <div className="w-32">
          <Sort
            value={sort}
            sortTypes={SORT_TYPES}
            onValueChange={sortType => setSort(sortType)}
            parseValue={sortType => (sortType === 'ASC' ? 'Earliest' : 'Recent')}
            showIcon={false}
            disabled={false}
          />
        </div>
      </div>
      <CommentBar releaseFrag={releaseFrag} sticky={null} />
      <div className="-mx-1 flex flex-col gap-6">
        <div>
          {data.map(item => (
            <CommentItem key={item.id} item={item} releaseFrag={releaseFrag} />
          ))}
        </div>
        {loading && (
          <div className="flex flex-col gap-4 pt-[10px]">
            {[...Array(3)].map((_, index) => (
              <UserCommentItemSkeleton key={index} />
            ))}
          </div>
        )}
        {hasNextPage && (
          <Button
            label="See all"
            size="S"
            className="self-center"
            variant="tertiary"
            onClick={onSeeAll}
          />
        )}
      </div>
    </div>
  );
}
